import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BlogEditor from '../../layout/components/blog/BlogEditor';
import ImageUpload from '../../components/upload';
import { useBlog } from '../../hooks/blogs';
import { updateBlog } from '../../api/blog';
import toast from "react-hot-toast";
import axios from 'axios';
import { base_URL } from '../../config/config';
import { FaArrowLeft } from "react-icons/fa";

const EditBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, refetch, isLoading } = useBlog(id);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [imageUrl, setImageURL] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    refetch();
  }, [id]);

  useEffect(() => {
    if (data) {
      setTitle(data.title || '');
      setSlug(data.slug || '');
      setSeoTitle(data.seoTitle || '');
      setSeoDescription(data.seoDescription || '');
      setContent(data.content || '');
      setImageURL(data.heroImageUrl || '');

      // Fetch HTML content from S3
      if (data.content) {
        axios.post(`${base_URL}/api/blogs/getHtmlFromS3`, {
          fileName: data.content
        })
        .then(response => {
          const { body } = response.data;
          if (body) setContent(body);
        })
        .catch(error => {
          console.error("Error fetching blog HTML content", error);
          toast.error("Failed to load preview content");
        });
      }
    }
  }, [data]);

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);
    const cleanSlug = value
      .toLowerCase()
      .replace(/[’']/g, "")
      .replace(/:/g, "")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");

      setSlug(cleanSlug);
    // setSlug(value.toLowerCase().replace(/\s+/g, '-'));
  };

  const handleSaveDraft = () => {
    const draft = {
      title,
      slug,
      seoTitle,
      seoDescription,
      content,
      heroImageUrl: imageUrl,
      status: 'draft',
      updatedAt: new Date().toISOString(),
    };
    updateBlog(data?._id, draft).then(resp => {
      if (resp?.error) toast.error(resp.error);
      else {
        toast.success("Draft saved successfully.");
        navigate("/blogs");
      }
    });
  };

  const handleSlug = (slugStr) => {
    const cleanSlug = slugStr
      .toLowerCase()
      .replace(/[’']/g, "")
      .replace(/:/g, "")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");

      setSlug(cleanSlug);
  };

  const handleUpdate = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Title is required";
    if (!slug.trim()) newErrors.slug = "Slug is required";
    if (!seoTitle.trim()) newErrors.seoTitle = "SEO Title is required";
    if (!seoDescription.trim()) newErrors.seoDescription = "SEO Description is required";
    if (!content.trim()) newErrors.content = "Content is required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the validation errors");
      return;
    }

    const blog = {
      title,
      slug,
      seoTitle,
      seoDescription,
      content,
      heroImageUrl: imageUrl,
      status: 'published',
      updatedAt: new Date().toISOString(),
    };

    updateBlog(data?._id, blog).then(resp => {
      if (resp?.error) toast.error(resp.error);
      else {
        toast.success("Blog published successfully.");
        navigate("/blogs");
      }
    });
  };

  if (isLoading || !data) {
    return <div className="text-center py-10">Loading blog data...</div>;
  }

  return (
    <div className='w-full h-full bg-white py-6'>
      <div className="max-w-4xl mx-auto mb-4">
				<button
					onClick={() => navigate(-1)}
					className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
				>
					<FaArrowLeft /> Back
				</button>
			</div>
      
      <div className="max-w-4xl mx-auto p-6 space-y-6 bg-white rounded-lg shadow-lg border border-neutral-200">
        <h1 className="text-2xl font-semibold">Edit Blog</h1>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            className="w-full border border-gray-300 rounded-md py-3 px-4 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Blog Title"
          />
          {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Slug <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => handleSlug(e.target.value)}
            className="w-full border border-gray-300 rounded-md py-3 px-4 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="URL Slug"
          />
          {errors.slug && <p className="text-red-500 text-sm mt-1">{errors.slug}</p>}
        </div>

        {/* SEO Title */}
        <div>
          <label className="block text-sm font-medium mb-1">
            SEO Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={seoTitle}
            onChange={(e) => setSeoTitle(e.target.value)}
            className="w-full border border-gray-300 rounded-md py-3 px-4 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="SEO Title"
          />
          {errors.seoTitle && <p className="text-red-500 text-sm mt-1">{errors.seoTitle}</p>}
        </div>

        {/* SEO Description */}
        <div>
          <label className="block text-sm font-medium mb-1">
            SEO Description <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={seoDescription}
            onChange={(e) => setSeoDescription(e.target.value)}
            className="w-full border border-gray-300 rounded-md py-3 px-4 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="SEO Description"
          />
          {errors.seoDescription && <p className="text-red-500 text-sm mt-1">{errors.seoDescription}</p>}
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium mb-1">Upload Hero Image</label>
          <ImageUpload
            className="bg-gray-100 border border-gray-300 rounded-md p-4 text-center cursor-pointer hover:bg-gray-200 transition"
            setImageURL={setImageURL}
            setUploading={setUploading}
            setProgress={setUploadProgress}
          >
            Click to upload image
          </ImageUpload>

          {uploading && (
            <div className="mt-2 w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-500 h-3 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {imageUrl && (
            <div className="mt-4">
              <img
                src={imageUrl}
                alt="Uploaded preview"
                className="w-full max-w-md rounded-md shadow-md"
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Content <span className="text-red-500">*</span>
          </label>
          <BlogEditor value={content} setValue={setContent} readOnly={false} />
          {errors.content && <p className="text-red-500 text-sm mt-1">{errors.content}</p>}
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-4 pt-4">
          <button
            onClick={handleSaveDraft}
            className="px-5 py-2 rounded-md bg-gray-300 hover:bg-gray-400 text-black transition"
          >
            Save as Draft
          </button>
          <button
            onClick={handleUpdate}
            className="px-5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition"
          >
            Publish
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditBlog;
